import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios'
import toast from 'react-hot-toast'

import { API_CONFIG } from '@/config/api'
import { authService } from '../auth/authService'
import { sessionManager, SessionExpiredError } from './sessionManager'
import { isAuthReady, waitForAuthReady } from './authReadiness'
import { announcePersonaRefusal, personaRefusalFrom } from './personaRefusal'

// Track 401 retry attempts to prevent infinite loops.
// Uses a custom config property instead of WeakMap because Axios creates
// new config objects on each request (merging defaults), which breaks
// WeakMap-based tracking and can cause infinite retry loops.
const MAX_401_RETRIES = 1
const RETRY_COUNT_KEY = '__retryCount'

// Global refresh promise that all 401 handlers can wait on
let refreshPromise: Promise<string | null> | null = null
let refreshPromiseResolve: ((token: string | null) => void) | null = null

class ApiClient {
  private client: AxiosInstance
  private isRefreshing = false
  private failedQueue: Array<{
    resolve: (token: string | null) => void
    reject: (error: Error) => void
  }> = []
  private tokenBeforeRefresh: string | null = null

  constructor() {
    this.client = axios.create({
      baseURL: API_CONFIG.baseURL,
      timeout: API_CONFIG.timeout,
      headers: API_CONFIG.headers,
    })

    this.setupInterceptors()
  }

  private processQueue(error: Error | null, token: string | null = null) {
    this.failedQueue.forEach(({ resolve, reject }) => {
      if (error) {
        reject(error)
      } else {
        resolve(token)
      }
    })
    this.failedQueue = []
  }

  private setupInterceptors() {
    // Request interceptor to add auth token and test user headers
    this.client.interceptors.request.use(
      async (config) => {
        // Boot gate: hold authenticated requests until a usable token exists
        // (or auth is known unrecoverable) — see authReadiness.ts. Auth
        // endpoints skip the gate so login flows never wait on themselves.
        if (!config.url?.includes('/auth/') && !isAuthReady()) {
          await waitForAuthReady()
        }
        const token = authService.getToken()
        if (token) {
          config.headers.Authorization = `Bearer ${token}`
        }
        
        // Add test user headers from selected persona (dev mode OR when VITE_ENABLE_TEST_USERS is set)
        // This allows UAT testing with test personas in deployed environments
        const enableTestUsers = import.meta.env.DEV || import.meta.env.VITE_ENABLE_TEST_USERS === 'true'
        if (enableTestUsers) {
          const devPersonaStr = localStorage.getItem('dev_selected_persona')
          if (devPersonaStr) {
            try {
              const devPersona = JSON.parse(devPersonaStr)
              config.headers['X-Test-User-Id'] = devPersona.personnelNumber || 'TEST001'
              config.headers['X-Test-User-Roles'] = (devPersona.roles || ['employee']).join(',')
              if (devPersona.permissions?.length) {
                config.headers['X-Test-User-Permissions'] = devPersona.permissions.join(',')
              }
            } catch (e) {
              console.warn('Failed to parse dev persona for API headers')
            }
          }
        }
        
        return config
      },
      (error) => {
        return Promise.reject(error)
      }
    )

    // Response interceptor for error handling with graceful 401 handling
    this.client.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config
        
        if (!originalRequest) {
          return Promise.reject(error)
        }
        
        // If session is already marked as expired, don't retry - just reject
        if (sessionManager.isExpired()) {
          if (import.meta.env.DEV) console.log('⏭️ apiClient: Session expired, skipping retry')
          return Promise.reject(new SessionExpiredError())
        }

        // Track retry count via custom config property (survives Axios config merging)
        const currentRetryCount = (originalRequest as any)[RETRY_COUNT_KEY] || 0

        // Handle 401 errors - attempt token refresh before giving up
        if (error.response?.status === 401 && currentRetryCount < MAX_401_RETRIES) {
          // Don't retry for auth endpoints (login, logout, etc.)
          if (originalRequest.url?.includes('/auth/')) {
            return Promise.reject(error)
          }

          // Increment retry count on the config object itself
          ;(originalRequest as any)[RETRY_COUNT_KEY] = currentRetryCount + 1

          // If we're already refreshing, queue this request
          if (this.isRefreshing) {
            return new Promise((resolve, reject) => {
              this.failedQueue.push({ resolve, reject })
            }).then((token) => {
              if (token && originalRequest.headers) {
                originalRequest.headers['Authorization'] = `Bearer ${token}`
              }
              return this.client(originalRequest)
            })
          }

          this.isRefreshing = true
          this.tokenBeforeRefresh = localStorage.getItem('access_token')

          // Create a promise that AuthProvider will resolve when refresh completes
          refreshPromise = new Promise<string | null>((resolve) => {
            refreshPromiseResolve = resolve
          })

          try {
            // Dispatch a custom event that AuthProvider can listen to
            // This allows the MSAL instance to handle the refresh
            if (import.meta.env.DEV) console.log('📡 apiClient: 401 received, dispatching token refresh event')
            if (import.meta.env.DEV) console.log('📡 Token before refresh:', this.tokenBeforeRefresh?.substring(0, 50) + '...')
            
            const refreshEvent = new CustomEvent('tokenRefreshNeeded')
            window.dispatchEvent(refreshEvent)
            
            // Wait for AuthProvider to signal completion (with timeout).
            // Must outlast AuthProvider's bounded MSAL settle-wait (8s) PLUS
            // the silent acquisition itself — a shorter timeout here reads a
            // slow-but-successful refresh as failure and marks the session
            // expired for nothing.
            const timeoutPromise = new Promise<string | null>((resolve) => {
              setTimeout(() => resolve(null), 15000)
            })
            
            const newToken = await Promise.race([refreshPromise, timeoutPromise])
            
            if (import.meta.env.DEV) console.log('📡 Token after refresh:', newToken?.substring(0, 50) + '...')
            
            // Check if session was marked expired during refresh
            if (sessionManager.isExpired()) {
              if (import.meta.env.DEV) console.log('🔴 apiClient: Session marked expired during refresh')
              this.processQueue(new SessionExpiredError())
              // Don't show toast here - SessionExpiredModal will handle UX
              return Promise.reject(new SessionExpiredError())
            }
            
            if (newToken && originalRequest.headers) {
              if (newToken !== this.tokenBeforeRefresh) {
                if (import.meta.env.DEV) console.log('✅ apiClient: Got new token, retrying request')
              } else {
                // Token unchanged after refresh — MSAL id tokens may not change on
                // silent refresh. The token could still be perfectly valid (the
                // original 401 may have been a transient gateway/validation hiccup).
                // Retry once instead of immediately marking session expired.
                if (import.meta.env.DEV) console.log('⚠️ apiClient: Token unchanged after refresh — retrying with same token')
              }
              originalRequest.headers['Authorization'] = `Bearer ${newToken}`
              this.processQueue(null, newToken)
              return this.client(originalRequest)
            } else {
              // No new token - session is expired
              console.warn('⚠️ apiClient: No token received after refresh')
              this.processQueue(new Error('Token refresh failed'))
              // Mark session as expired - the modal will handle user interaction
              sessionManager.markExpired()
            }
          } catch (refreshError) {
            this.processQueue(refreshError as Error)
            console.error('Token refresh failed in apiClient:', refreshError)
          } finally {
            this.isRefreshing = false
            refreshPromise = null
            refreshPromiseResolve = null
          }
        } else if (error.response?.status === 401 && currentRetryCount >= MAX_401_RETRIES) {
          // Exhausted retries — token is genuinely expired
          console.warn('⚠️ apiClient: 401 after retry — session is expired')
          sessionManager.markExpired()
        } else if (error.response?.status === 403) {
          const personaRefusal = originalRequest.headers?.['X-Test-User-Id']
            ? personaRefusalFrom(403, (error.response.data as { detail?: unknown } | undefined)?.detail)
            : null
          if (personaRefusal) {
            // The test persona itself was refused: AuthProvider drops it and says why.
            announcePersonaRefusal(personaRefusal)
          } else {
            // Stable id dedupes the toast — a page whose every mount-time
            // request 403s (e.g. a user without the module's role) shows
            // ONE toast instead of one per rejected request.
            toast.error('You do not have permission to perform this action.', {
              id: 'forbidden',
            })
          }
        } else if (error.response?.status && error.response.status >= 500) {
          toast.error('Server error. Please try again later.')
        } else if (error.code === 'ECONNABORTED') {
          toast.error('Request timeout. Please check your connection.')
        } else if (!error.response) {
          // Network error - use id to prevent duplicate toasts
          toast.error('Network error. Please check your connection.', {
            id: 'network-error',
          })
        }

        return Promise.reject(error)
      }
    )
  }

  async get<T>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.client.get<T>(url, config)
  }

  async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.client.post<T>(url, data, config)
  }

  async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.client.put<T>(url, data, config)
  }

  async patch<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.client.patch<T>(url, data, config)
  }

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.client.delete<T>(url, config)
  }
}

export const apiClient = new ApiClient()

// Boot-gate coordination (see authReadiness.ts) — re-exported here because
// app-side shims alias '@/services/apiClient' to this module.
export { isAuthReady, waitForAuthReady, signalAuthTokenReady, decodeJwtExp } from './authReadiness'
export { PERSONA_REFUSED_EVENT, personaRefusalFrom } from './personaRefusal'
export type { PersonaRefusal } from './personaRefusal'

// Export function for AuthProvider to signal refresh completion
export function signalTokenRefreshComplete(token: string | null): void {
  if (refreshPromiseResolve) {
    if (import.meta.env.DEV) console.log('📡 apiClient: Token refresh signaled complete')
    refreshPromiseResolve(token)
  }
  // Broadcast to authenticatedFetch listeners (and any future consumers)
  window.dispatchEvent(new CustomEvent('tokenRefreshComplete', {
    detail: { token }
  }))
}
